import { Unit } from '../learnKorean/types';

/** Hiragana, the first script: every JLPT N5 question can be read in it. */
export const HIRAGANA: Unit = {
  id: 'hiragana',
  number: 1,
  title: 'Hiragana',
  description: 'The first Japanese script. Learn the 46 letters, the sound marks and read your first words.',
  emoji: 'あ',
  lessons: [
    {
      id: 'vowels',
      title: 'Vowels',
      summary: 'あ い う え お, the five vowels',
      exercises: [
        { kind: 'teach', ko: 'あ', roman: 'a', en: 'Sounds like the "a" in "father"', note: 'Japanese has only five vowel sounds, always said the same way. Every other letter ends in one of them.' },
        { kind: 'teach', ko: 'い', roman: 'i', en: 'Sounds like the "ee" in "see"' },
        { kind: 'teach', ko: 'う', roman: 'u', en: 'Sounds like the "oo" in "moon", with lips not rounded' },
        { kind: 'teach', ko: 'え', roman: 'e', en: 'Sounds like the "e" in "pen"' },
        { kind: 'teach', ko: 'お', roman: 'o', en: 'Sounds like the "o" in "go", but short' },
        { kind: 'choice', prompt: 'Which sound does this letter make?', ko: 'え', options: ['e', 'i', 'a', 'o'], answer: 0 },
        { kind: 'match', prompt: 'Match each letter to its sound', pairs: [['あ', 'a'], ['い', 'i'], ['う', 'u'], ['お', 'o']] },
        { kind: 'choice', prompt: 'Listen. Which letter did you hear?', speak: 'う', listenOnly: true, options: ['あ', 'う', 'え', 'お'], answer: 1, explain: 'う is "u".' },
        { kind: 'choice', prompt: 'Which sound does this letter make?', ko: 'お', options: ['a', 'u', 'o', 'e'], answer: 2 },
        { kind: 'choice', prompt: 'Listen. Which letter did you hear?', speak: 'い', listenOnly: true, options: ['い', 'え', 'あ', 'う'], answer: 0, explain: 'い is "i".' },
      ],
    },
    {
      id: 'k-s',
      title: 'K and S',
      summary: 'か き く け こ, さ し す せ そ',
      exercises: [
        { kind: 'teach', ko: 'か · き · く · け · こ', speak: 'か、き、く、け、こ', roman: 'ka · ki · ku · ke · ko', en: 'K with each vowel', note: 'Each letter is a whole syllable: a consonant and a vowel.' },
        { kind: 'teach', ko: 'さ · し · す · せ · そ', speak: 'さ、し、す、せ、そ', roman: 'sa · shi · su · se · so', en: 'S with each vowel', note: 'Watch し: it is "shi", not "si".' },
        { kind: 'choice', prompt: 'Which sound does this letter make?', ko: 'き', options: ['ki', 'ka', 'ke', 'shi'], answer: 0 },
        { kind: 'choice', prompt: 'Which sound does this letter make?', ko: 'し', options: ['su', 'shi', 'sa', 'chi'], answer: 1 },
        { kind: 'match', prompt: 'Match each letter to its sound', pairs: [['か', 'ka'], ['こ', 'ko'], ['す', 'su'], ['せ', 'se']] },
        { kind: 'choice', prompt: 'Listen. Which letter did you hear?', speak: 'け', listenOnly: true, options: ['き', 'こ', 'け', 'く'], answer: 2, explain: 'け is "ke".' },
        { kind: 'choice', prompt: 'Listen. Which letter did you hear?', speak: 'そ', listenOnly: true, options: ['そ', 'さ', 'す', 'せ'], answer: 0, explain: 'そ is "so".' },
        { kind: 'teach', ko: 'あさ', roman: 'a-sa', en: 'morning', picture: '🌅' },
        { kind: 'teach', ko: 'いけ', roman: 'i-ke', en: 'pond' },
        { kind: 'choice', prompt: 'How do you read this word?', ko: 'あさ', options: ['asa', 'aka', 'isa', 'oso'], answer: 0 },
      ],
    },
    {
      id: 't-n',
      title: 'T and N',
      summary: 'た ち つ て と, な に ぬ ね の',
      exercises: [
        { kind: 'teach', ko: 'た · ち · つ · て · と', speak: 'た、ち、つ、て、と', roman: 'ta · chi · tsu · te · to', en: 'T with each vowel', note: 'Two are irregular: ち is "chi" and つ is "tsu".' },
        { kind: 'teach', ko: 'な · に · ぬ · ね · の', speak: 'な、に、ぬ、ね、の', roman: 'na · ni · nu · ne · no', en: 'N with each vowel' },
        { kind: 'choice', prompt: 'Which sound does this letter make?', ko: 'つ', options: ['tsu', 'su', 'tu', 'chi'], answer: 0 },
        { kind: 'choice', prompt: 'Which sound does this letter make?', ko: 'の', options: ['na', 'ne', 'no', 'nu'], answer: 2 },
        { kind: 'match', prompt: 'Match each letter to its sound', pairs: [['た', 'ta'], ['ち', 'chi'], ['に', 'ni'], ['ね', 'ne']] },
        { kind: 'choice', prompt: 'Listen. Which letter did you hear?', speak: 'と', listenOnly: true, options: ['た', 'て', 'と', 'つ'], answer: 2, explain: 'と is "to".' },
        { kind: 'teach', ko: 'いぬ', roman: 'i-nu', en: 'dog', picture: '🐕' },
        { kind: 'teach', ko: 'ねこ', roman: 'ne-ko', en: 'cat', picture: '🐈' },
        { kind: 'teach', ko: 'くち', roman: 'ku-chi', en: 'mouth', picture: '👄' },
        { kind: 'teach', ko: 'て', roman: 'te', en: 'hand', picture: '✋' },
        { kind: 'choice', prompt: 'Which picture is いぬ?', ko: 'いぬ', options: ['🐕', '🐈', '👄', '✋'], pictureOptions: true, answer: 0 },
      ],
    },
    {
      id: 'h-m',
      title: 'H and M',
      summary: 'は ひ ふ へ ほ, ま み む め も',
      exercises: [
        { kind: 'teach', ko: 'は · ひ · ふ · へ · ほ', speak: 'は、ひ、ふ、へ、ほ', roman: 'ha · hi · fu · he · ho', en: 'H with each vowel', note: 'ふ is between "fu" and "hu", with soft lips. は is read "wa" when it marks the topic of a sentence, and へ "e" when it means "to".' },
        { kind: 'teach', ko: 'ま · み · む · め · も', speak: 'ま、み、む、め、も', roman: 'ma · mi · mu · me · mo', en: 'M with each vowel' },
        { kind: 'choice', prompt: 'Which sound does this letter make?', ko: 'ふ', options: ['hu / fu', 'ho', 'hi', 'mu'], answer: 0 },
        { kind: 'choice', prompt: 'Which sound does this letter make?', ko: 'め', options: ['mi', 'me', 'nu', 'ne'], answer: 1, explain: 'め is "me". ぬ (nu) looks like it, with a loop at the end.' },
        { kind: 'match', prompt: 'Match each letter to its sound', pairs: [['は', 'ha'], ['ほ', 'ho'], ['み', 'mi'], ['も', 'mo']] },
        { kind: 'teach', ko: 'はな', roman: 'ha-na', en: 'flower', picture: '🌸' },
        { kind: 'teach', ko: 'みみ', roman: 'mi-mi', en: 'ear', picture: '👂' },
        { kind: 'teach', ko: 'ふね', roman: 'fu-ne', en: 'ship', picture: '🚢' },
        { kind: 'teach', ko: 'まめ', roman: 'ma-me', en: 'beans', picture: '🫘' },
        { kind: 'choice', prompt: 'How do you read this word?', ko: 'はな', options: ['hana', 'hama', 'nana', 'hono'], answer: 0 },
      ],
    },
    {
      id: 'y-r-w',
      title: 'Y, R, W and N',
      summary: 'や ゆ よ, ら り る れ ろ, わ を ん',
      exercises: [
        { kind: 'teach', ko: 'や · ゆ · よ', speak: 'や、ゆ、よ', roman: 'ya · yu · yo', en: 'Y has only three letters' },
        { kind: 'teach', ko: 'ら · り · る · れ · ろ', speak: 'ら、り、る、れ、ろ', roman: 'ra · ri · ru · re · ro', en: 'R with each vowel', note: 'The Japanese R is a light tap, between "r", "l" and "d".' },
        { kind: 'teach', ko: 'わ · を · ん', speak: 'わ、を、ん', roman: 'wa · o · n', en: 'The last three letters', note: 'を is read "o" and only marks the object of a verb. ん is the only letter with no vowel.' },
        { kind: 'choice', prompt: 'Which sound does this letter make?', ko: 'る', options: ['ro', 'ru', 're', 'ra'], answer: 1 },
        { kind: 'choice', prompt: 'Which sound does this letter make?', ko: 'ん', options: ['n', 'wa', 'yu', 'mu'], answer: 0 },
        { kind: 'match', prompt: 'Match each letter to its sound', pairs: [['や', 'ya'], ['よ', 'yo'], ['り', 'ri'], ['わ', 'wa']] },
        { kind: 'teach', ko: 'やま', roman: 'ya-ma', en: 'mountain', picture: '⛰️' },
        { kind: 'teach', ko: 'さくら', roman: 'sa-ku-ra', en: 'cherry blossom', picture: '🌸' },
        { kind: 'teach', ko: 'ほん', roman: 'ho-n', en: 'book', picture: '📕' },
        { kind: 'teach', ko: 'くるま', roman: 'ku-ru-ma', en: 'car', picture: '🚗' },
        { kind: 'choice', prompt: 'Which picture is くるま?', ko: 'くるま', options: ['⛰️', '🚗', '📕', '🌸'], pictureOptions: true, answer: 1 },
      ],
    },
    {
      id: 'sound-marks',
      title: 'Sound marks',
      summary: 'が ざ だ ば ぱ: two dots and a circle',
      exercises: [
        { kind: 'teach', ko: 'か → が', speak: 'が', roman: 'ka → ga', en: 'Two small dots (゛) make a sound voiced', note: 'K becomes G, S becomes Z, T becomes D, H becomes B.' },
        { kind: 'teach', ko: 'が · ざ · だ · ば', speak: 'が、ざ、だ、ば', roman: 'ga · za · da · ba', en: 'G, Z, D and B' },
        { kind: 'teach', ko: 'は → ぱ', speak: 'ぱ', roman: 'ha → pa', en: 'A small circle (゜) on H makes P' },
        { kind: 'choice', prompt: 'Which sound does this letter make?', ko: 'ご', options: ['go', 'ko', 'zo', 'do'], answer: 0 },
        { kind: 'choice', prompt: 'Which sound does this letter make?', ko: 'ぷ', options: ['bu', 'fu', 'pu', 'mu'], answer: 2 },
        { kind: 'choice', prompt: 'Listen. Which letter did you hear?', speak: 'ば', listenOnly: true, options: ['は', 'ぱ', 'ば', 'ま'], answer: 2, explain: 'ば is "ba": は with two dots.' },
        { kind: 'teach', ko: 'かぎ', roman: 'ka-gi', en: 'key', picture: '🔑' },
        { kind: 'teach', ko: 'かぜ', roman: 'ka-ze', en: 'wind; a cold', picture: '🌬️' },
        { kind: 'teach', ko: 'でんわ', roman: 'de-n-wa', en: 'telephone', picture: '☎️' },
        { kind: 'teach', ko: 'えんぴつ', roman: 'e-n-pi-tsu', en: 'pencil', picture: '✏️' },
        { kind: 'match', prompt: 'Match the word to its meaning', pairs: [['かぎ', 'key'], ['でんわ', 'telephone'], ['えんぴつ', 'pencil'], ['かぜ', 'wind; a cold']] },
      ],
    },
    {
      id: 'small-letters',
      title: 'Small letters',
      summary: 'きゃ, しゅ, ちょ and the small っ',
      exercises: [
        { kind: 'teach', ko: 'き + ゃ = きゃ', speak: 'きゃ', roman: 'ki + ya = kya', en: 'A small ゃ ゅ ょ joins the letter before it into one sound' },
        { kind: 'teach', ko: 'しゃ · しゅ · しょ', speak: 'しゃ、しゅ、しょ', roman: 'sha · shu · sho', en: 'Small ya, yu, yo after し' },
        { kind: 'teach', ko: 'ちゃ · ちゅ · ちょ', speak: 'ちゃ、ちゅ、ちょ', roman: 'cha · chu · cho', en: 'Small ya, yu, yo after ち' },
        { kind: 'teach', ko: 'きって', roman: 'ki-t-te', en: 'stamp', picture: '📮', note: 'A small っ is a short pause: the next sound is doubled. きって is "kitte", not "kitsute".' },
        { kind: 'teach', ko: 'おちゃ', roman: 'o-cha', en: 'green tea', picture: '🍵' },
        { kind: 'teach', ko: 'でんしゃ', roman: 'de-n-sha', en: 'train', picture: '🚃' },
        { kind: 'teach', ko: 'がっこう', roman: 'ga-k-kō', en: 'school', picture: '🏫', note: 'こう is a long "o": hold it for two beats. Long vowels change meaning in Japanese.' },
        { kind: 'choice', prompt: 'How do you read this word?', ko: 'きって', options: ['kitte', 'kitsute', 'kite', 'kiite'], answer: 0 },
        { kind: 'choice', prompt: 'How do you read this word?', ko: 'でんしゃ', options: ['densha', 'denshiya', 'densya', 'dencha'], answer: 0 },
        { kind: 'choice', prompt: 'Which picture is おちゃ?', ko: 'おちゃ', options: ['📮', '🍵', '🚃', '🏫'], pictureOptions: true, answer: 1 },
        { kind: 'match', prompt: 'Match the word to its meaning', pairs: [['きって', 'stamp'], ['おちゃ', 'green tea'], ['でんしゃ', 'train'], ['がっこう', 'school']] },
      ],
    },
  ],
};
